import { MouseEventHandler, useMemo } from 'react'
import Highlighter from 'react-highlight-words'
import { TbColumns } from 'react-icons/tb'
import { Checkbox } from '~/@/components/ui/checkbox-v2'
import { cn } from '~/@/lib/utils'
import { amber } from '~/common/styles/colors'
import {
  resolveCellValues,
  useLookupContext,
} from '~/components/records/lookup/hooks/LookupProvider'
import { LookupTableRow } from '~/components/records/types'
import { EnumValueMark, getEnumValueColorAndIcon } from '~/components/shared/enum-options'
import { EnumOptionMark } from '~/components/shared/EnumOptionMark'

interface LookupMenuItemProps {
  searchQuery: string
  option: LookupTableRow
  isHighlighted: boolean
  columnId: string
  handleClick?: MouseEventHandler<HTMLDivElement> | undefined
  selected: boolean
}

const NO_VALUE = 'No value'

const HIGHLIGHT_STYLE = { backgroundColor: amber[5], color: amber[12] }

/** Single row in the lookup picker's left pane. */
export const LookupMenuItem = ({
  option,
  selected = false,
  isHighlighted,
  searchQuery,
  columnId,
  handleClick,
}: LookupMenuItemProps) => {
  const { getCellDisplay, getEnumSource } = useLookupContext()

  const columnCell = useMemo(
    () => option.cells.find((cell) => cell.column.id === columnId),
    [columnId, option.cells],
  )

  const optionDisplay = useMemo((): string => {
    const display = columnCell ? getCellDisplay(columnCell, columnCell.column.id) : null
    return display?.trim() || NO_VALUE
  }, [columnCell, getCellDisplay])

  const enumValues = useMemo((): EnumValueMark[] => {
    if (!columnCell) {
      return []
    }
    const enumSource = getEnumSource(columnCell, columnCell.column.id)
    if (!enumSource) {
      return []
    }
    return resolveCellValues(columnCell).map((label) => ({
      label,
      ...getEnumValueColorAndIcon(label, enumSource.options),
    }))
  }, [columnCell, getEnumSource])

  const isNoValueOption = optionDisplay === NO_VALUE

  return (
    <div
      className={cn('cursor-pointer rounded px-2 py-1.5 transition-colors', {
        'bg-gray-3': selected,
        'bg-gray-2': isHighlighted && !selected,
        'hover:bg-gray-2': !selected,
      })}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        {/* Fixed-size slot for the leading glyph keeps row height stable as it
            swaps between the table icon and the selected checkbox. */}
        <div className="flex size-4 shrink-0 items-center justify-center">
          {selected ? (
            <Checkbox
              checked
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                handleClick?.(e as React.MouseEvent<HTMLDivElement>)
              }}
              variant="primary"
            />
          ) : (
            <TbColumns size={16} />
          )}
        </div>
        {enumValues.length > 0 ? (
          enumValues.map((enumValue, index) => (
            <div key={enumValue.label} className="flex min-w-0 items-center gap-2">
              <EnumOptionMark color={enumValue.color} iconName={enumValue.iconName} />
              <Highlighter
                autoEscape
                className="truncate text-xs text-gray-11"
                highlightStyle={HIGHLIGHT_STYLE}
                searchWords={[searchQuery]}
                textToHighlight={
                  index < enumValues.length - 1 ? `${enumValue.label},` : enumValue.label
                }
              />
            </div>
          ))
        ) : (
          <Highlighter
            autoEscape
            className={cn('truncate text-xs', isNoValueOption ? 'text-gray-7' : 'text-gray-11')}
            highlightStyle={HIGHLIGHT_STYLE}
            searchWords={[searchQuery]}
            textToHighlight={optionDisplay}
          />
        )}
      </div>
    </div>
  )
}
